import { useState, useEffect, ChangeEvent, useMemo } from "react";
import { useParams } from "react-router";

import { useApiClient } from "../../../data/clients/ApiClient";
import {
  CartItemDesign,
  CartItemDesignInstance,
  CartItemDesignInstanceLogo,
  DesignSize,
} from "../order-create/models/CartItem";
import { ProductPreviewDesign } from "../order-create/subcomponents/ProductPreviewDesign";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { ImageContainer } from "../../components/image/ImageContainer";
import { SecondaryButton } from "../../components/Buttons/SecondaryButton";
import { DesignInstanceComponent } from "../order-create/subcomponents/DesignInstance.Component";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { DesignInstanceVM } from "../order-create/subcomponents/DesignInstance.VM";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getLogoType } from "../../../core/models/entities/BaseLogo";
import { DesignLogo } from "../../../core/models/entities/Design";
import { ThankYouFrame } from "./ExternalApprovalFlow";
import { getDesignLogoPlacementMap } from "../order-create/functions/getDesignLogoPlacement";

// HACK:
// This component is the one of the worst abominations in this project, created from a variety of code snippets from existing components
//  that are incompatible with the data model or lacks the access to local contexts or authorization.
// Refactor this if you must, but be aware that almost every line of code in here exist somewhere else in the codebase as well.
// Due to less than insufficient time and the inability to fully reuse existing components,
//  this is the solution that can feasibly be implemented at this moment.
// The correct solution would include refactoring all the relevant components in order to reuse them here,
//  however doing so will be a great undertaking and may affect a lot of code around where those components are already used.
export default function ExternalCustomerDesignEditor() {
  const { entryKey } = useParams();
  const apiClient = useApiClient();
  const [orderline, setOrderline] = useState<CartItemDesign>();
  const [success, setSuccess] = useState(false);
  const [logos, setLogos] = useState<CartItemDesignInstanceLogo[]>([]);

  const placementLabels = useMemo(() => {
    if (!orderline) {
      return new Map<string, string>();
    }

    return getDesignLogoPlacementMap(orderline.design);
  }, [orderline]);

  const designName = orderline?.design?.title;
  const manufacturer = orderline?.design?.style?.manufacturer?.name;
  const styleModel = orderline?.design?.style?.modelAndNameWithoutHyphen;

  useEffect(() => {
    if (!entryKey) return;
    (async () => {
      const external = await apiClient.getPublicDesignEntry(entryKey);
      if (external.design)
        setOrderline({
          id: external.id,
          title: external.title,
          design: external.design,
          casing: external.casing,
          // @ts-ignore
          designId: external.design.id,
          displayImage: external.design.displayImage,
          type: "Design",
          instances: [],
        });
    })();
  }, [entryKey]);

  function mapLogoToLogoSpec(
    designLogo: DesignLogo
  ): CartItemDesignInstanceLogo {
    const label =
      placementLabels.get(designLogo.inDesignId) ??
      designLogo.viewName?.toUpperCase?.() ??
      "";
    return {
      logoVariant: getLogoType(designLogo.logo),
      locationIdentifier: label,
      value: "",
      logoId: designLogo.logoId,
      designLogoInDesignId: designLogo.inDesignId,
    } as CartItemDesignInstanceLogo;
  }

  function removeRow(row: number): void {
    if (!orderline) return;
    const updated = {
      ...orderline,
      label: "",
      size: "M",
      instances: orderline?.instances.filter((_, index) => index !== row),
    };
    setOrderline(updated);
  }

  function addRow(): void {
    if (!orderline) return;
    const instance = {
      logos: orderline.design.designLogos.map(mapLogoToLogoSpec),
      label: "",
      toDelivery: 10,
      toStorage: 0,
      size: "M",
    };
    const instances = [...orderline.instances, instance];
    const updated = { ...orderline, instances };
    setOrderline(updated);
  }

  function updateInstance(
    index: number,
    instance: CartItemDesignInstance
  ): void {
    const instances = orderline?.instances;
    if (!(orderline && instances)) return;
    instances[index] = instance;
    const updated = {
      ...orderline,
      instances: instances,
    };
    setOrderline(updated);
  }

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!(file && entryKey && orderline)) return;

    const response = await apiClient.uploadPublicTemplate(entryKey, file);
    // HACK: `any` because I am not mapping this model again!
    const instances = response.designs[0]?.instances?.map((i: any) => {
      i.logos.forEach((l: any) => {
        const designLogo = orderline.design.designLogos.find(
          (dl) => dl.logoId === l.logoId
        );
        const placement = designLogo
          ? placementLabels.get(designLogo.inDesignId) ??
            designLogo.viewName?.toUpperCase?.() ??
            l.view
          : l.view;
        l.locationIdentifier = placement;
        l.logoVariant = logos.find((o) => o.logoId === l.logoId)?.logoVariant;
        l.designLogoInDesignId = designLogo?.inDesignId;
      });
      i.toDelivery = i.quantity;
      return i;
    });

    setOrderline({ ...orderline, instances });
  }

  function download() {
    if (!(entryKey && orderline)) return;
    apiClient.downloadPublicTemplate(entryKey, orderline).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `designs_${orderline.id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    });
  }

  async function submit() {
    if (!(entryKey && orderline)) return;
    const ok = await apiClient.postPublicDesignEntry(entryKey, orderline);
    if (ok) setSuccess(true);
  }

  useEffect(() => {
    if (orderline && orderline.instances.length === 0) {
      addRow();
      if (logos) setLogos(orderline.design.designLogos.map(mapLogoToLogoSpec));
    }
  }, [orderline]);

  if (!entryKey) return <>Invalid key</>;
  if (!orderline) return <></>;

  if (success) return <ThankYouFrame />;

  return (
    <div className="flex flex-col flex-1">
      <div id={"main-box"} className={"flex-1 flex space-x-10"}>
        <div id={"left-box"} className={"space-y-2 flex-1"}>
          <div className={"flex-1"}>
            {/* NOTE: Based on DesignDetails */}
            <div className={"space-y-2 w-full"}>
              <p
                className={"text-sm font-semibold"}
                style={{ lineHeight: "18px" }}
              >
                Product
              </p>
              <div className={"flex border rounded w-full space-x-5"}>
                {/* NOTE: By some miracle, we are able to reuse the ProductPreviewDesign component as-is */}
                <ProductPreviewDesign orderline={orderline} size={"lg"} />

                <div className={"flex flex-1 flex-col bg-gray-100 p-3"}>
                  <div className={"text-xs p-1 flex flex-col gap-1"}>
                    <p>
                      <b>Design Name</b> {designName}
                    </p>
                    <p>
                      <b>Manufacturer</b> {manufacturer}
                    </p>
                    <p>
                      <b>Model</b> {styleModel}
                    </p>
                  </div>
                  <div className={"flex-1"} />
                </div>
              </div>
            </div>

            {/* NOTE: Based on code segment in LogoPlacer */}
            <BoxContent
              className={
                "relative py-4 justify-center items-center !min-h-[60vh]"
              }
            >
              <div className={"relative flex-1"}>
                <ImageContainer
                  url={orderline.displayImage.highResUrl}
                  className="relative bg-cover shrink-0"
                />
              </div>
            </BoxContent>
          </div>
        </div>

        <div id={"right-box"} className={"flex-1 flex flex-col"}>
          {/* NOTE: Based on LogoTextEditor */}
          <div id={"text-numeric-edit"} className={"space-y-2 pb-2"}>
            <div
              id={"header"}
              className={"uppercase text-xs flex pr-8 pl-4 space-x-1 h-4"}
              style={{
                lineHeight: "18px",
                height: 18,
              }}
            >
              <div className={"w-[270px] font-bold"}>Names</div>
              <div className={"w-[90px] font-bold"}>Sizes</div>
              <div className={"w-[90px] font-bold"}>QTY</div>
            </div>
            <div className={"overflow-y-auto overflow-x-hidden max-h-[315px]"}>
              <div className={"border rounded bg-gray-100 space-y-0.5"}>
                {/* NOTE: Based on 'rows' snippet from LogoTextEditor */}
                {orderline.instances?.map((instance, instanceIndex) => (
                  <div className={"relative grid"} key={instanceIndex}>
                    <DesignInstanceComponent
                      casing={orderline.casing}
                      {...DesignInstanceVM({
                        index: instanceIndex,
                        instance: instance,
                        design: orderline.design,
                        updateInstance,
                      })}
                    />
                    <div
                      className={
                        "absolute h-full flex items-start right-20 pl-3 mr-[-25px] pt-2.5"
                      }
                    >
                      <div
                        onClick={() => removeRow(instanceIndex)}
                        className={"hide hover:text-status-failed-1"}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={"flex justify-between space-x-4"}>
              <PrimaryButton className="flex-1" onClick={addRow}>
                Add Row
              </PrimaryButton>
              {/* Because of white-labeling, we cannot use the dialog component as this feature normally does. 
                            Instead we mimic the button and use a raw file input */}
              <label className="relative button-root but-32 button-secondary flex-1">
                <input
                  type="file"
                  style={{ display: "none" }}
                  onChange={upload}
                />
                UPLOAD EXCEL FILE
              </label>
              <SecondaryButton
                className="flex-1"
                onClick={download}
                disabled={false}
              >
                DOWNLOAD TEMPLATE
              </SecondaryButton>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-row justify-end">
        <PrimaryButton onClick={submit}>I'M DONE</PrimaryButton>
      </div>
    </div>
  );
}
